import { Button } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import { APP_RATING, APP_STORE_URL, GOOGLE_PLAY_URL } from '@/constants/downloadApp';
import { SMART_LINKS } from '@/constants/smartLinkConstants';
import { isAndroidPhone, isIPhone, isServer } from '@/helpers/general';
import { openAppOrStore } from '@/helpers/smartLinks';
import { track } from '@/helpers/tracking';
import { _t } from '@/locale';
import QRCode from 'react-qr-code';

const baseTranslationKey = 'features.app-download-prompt';

export type AppDownloadPromptScreen = 'my_bookings_upcoming' | 'my_bookings_finished' | 'favourites' | 'place_details';

type Props = {
  screenName: AppDownloadPromptScreen;
};

const SCREENS = {
  my_bookings_upcoming: { copy: 'bookings', smartLink: () => SMART_LINKS.BOOKINGS },
  my_bookings_finished: { copy: 'bookings', smartLink: () => SMART_LINKS.BOOKINGS_FINISHED },
  favourites: { copy: 'favourites', smartLink: () => SMART_LINKS.FAVORITES },
  // The salon, not the Favourites list: the heart saved nothing on the web, so the visitor needs the
  // salon in front of them to save it in the app.
  place_details: { copy: 'favourites', smartLink: () => SMART_LINKS.getPlaceSmartLink(window.location.href) },
} as const;

const AppRating = () => (
  <div className="gap-md text-l text-fg_secondary flex items-center">
    <div className="gap-2xs flex items-center">
      <span>{APP_RATING.score}</span>
      <span className="gap-3xs flex" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((star) => (
          <Icon key={star} variant="star-filled" color="warning-500" size="xs" />
        ))}
      </span>
    </div>
    <span>{_t(`${baseTranslationKey}.ratingCount`, { thousands: Math.round(APP_RATING.count / 1000) })}</span>
  </div>
);

type Store = 'app_store' | 'google_play';

const StoreBadges = ({ onClick }: { onClick: (store: Store) => void }) => (
  <div className="gap-md flex items-center">
    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => onClick('app_store')}>
      <img src="/images/download-app-store.svg" className="h-[40px]" alt={_t('availableAppStore')} />
    </a>
    <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" onClick={() => onClick('google_play')}>
      <img src="/images/download-google-play.svg" className="h-[40px]" alt={_t('availablePlayStore')} />
    </a>
  </div>
);

/**
 * Stands in for My bookings and Favorites when nobody is logged in, and for the login prompt when a
 * logged-out visitor tries to save a favourite. A phone gets one button that hands over to the app
 * or its store; every other device gets a QR code to scan with a phone.
 */
const AppDownloadPrompt = ({ screenName }: Props) => {
  // The server can't tell a phone from a desktop; rendering nothing beats flashing the QR layout at
  // phones until the client renders.
  if (isServer) return null;

  const { copy } = SCREENS[screenName];
  const smartLink = SCREENS[screenName].smartLink();
  const copyKey = `${baseTranslationKey}.${copy}` as const;
  const isPhone = isIPhone() || isAndroidPhone();

  const trackClick = (button: 'download_app' | Store) => {
    track('Button Clicked', { screen_name: screenName, button_id: `${screenName}_${button}` });
  };

  const handleDownloadAppClick = () => {
    trackClick('download_app');
    openAppOrStore(smartLink);
  };

  const ratingAndBadges = (
    <div className="gap-md flex flex-col items-center">
      <AppRating />
      <StoreBadges onClick={trackClick} />
    </div>
  );

  if (isPhone) {
    return (
      // Short screens tighten the spacing so the store badges stay above the fold.
      <div className="gap-3xl pt-3xl [@media(max-height:820px)]:gap-xl [@media(max-height:820px)]:pt-xl flex w-full flex-col items-center">
        <div className="p-xl [@media(max-height:820px)]:p-md">
          <img
            src="/images/app-download-prompt/bokadirekt-app-icon.png"
            alt=""
            width={104}
            height={104}
            className="drop-shadow-lg"
          />
        </div>
        <div className="gap-md px-3xl flex w-full flex-col items-center">
          <h1 className="text-h-m text-fg_secondary text-center font-bold">{_t(`${copyKey}.title`)}</h1>
          <ul className="gap-xs flex flex-col items-center">
            {(['usp1', 'usp2', 'usp3'] as const).map((usp) => (
              <li key={usp} className="gap-2xs text-m text-fg_secondary flex items-start">
                <Icon variant="check" color="fg_link" style={{ width: 20, height: 20 }} />
                {_t(`${copyKey}.${usp}`)}
              </li>
            ))}
          </ul>
        </div>
        <div className="px-md w-full">
          <Button
            variant="primary"
            size="lg"
            block
            label={_t(`${baseTranslationKey}.cta`)}
            onClick={handleDownloadAppClick}
          />
        </div>
        {ratingAndBadges}
      </div>
    );
  }

  return (
    <div className="p-3xl gap-2xl mx-auto flex w-full max-w-[560px] flex-col items-center">
      <div className="gap-md flex flex-col items-center">
        <h1 className="text-h-xl text-fg_secondary text-center font-bold">{_t(`${copyKey}.title`)}</h1>
        <p className="px-3xl text-m text-fg_secondary text-center font-bold">{_t(`${copyKey}.scanQrCode`)}</p>
      </div>
      {/* Always dark on light, whatever the theme: scanners need the contrast and a light quiet zone. */}
      <div className="p-md border-border_regular bg-bg_neutral_alwaysLight text-fg_alwaysDark relative rounded-md border">
        {/* Error correction level H leaves enough redundancy to scan around the logo. */}
        <QRCode
          value={SMART_LINKS.forceRedirectToStore(smartLink)}
          level="H"
          size={125}
          fgColor="currentColor"
          bgColor="transparent"
        />
        <div className="bg-bg_neutral_alwaysLight p-2xs absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <Icon variant="bokadirekt" color="brand-500" size="sm" />
        </div>
      </div>
      {ratingAndBadges}
    </div>
  );
};

export default AppDownloadPrompt;
